import React, { useState, useEffect } from 'react';
import { useTransactions } from '../contexts/TransactionContext';
import Icon from './Icon';
import { getCategoryIcon } from '../utils/categoryIcons';

const CalendarView = () => {
  const { transactions } = useTransactions();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 월의 첫 번째 날과 마지막 날
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // 달력 시작일 (이전 달의 마지막 주 포함)
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());
  
  // 달력 종료일 (다음 달의 첫 주 포함)
  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

  // 달력에 표시할 날짜들 생성
  const calendarDays = [];
  const current = new Date(startDate);
  
  while (current <= endDate) {
    calendarDays.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  // 날짜별 거래 데이터 계산
  const getDateData = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    const dayTransactions = transactions.filter(t => {
      // 거래 날짜를 로컬 기준 YYYY-MM-DD 형식으로 변환
      const tDate = new Date(t.date);
      const transactionDate = `${tDate.getFullYear()}-${String(tDate.getMonth() + 1).padStart(2, '0')}-${String(tDate.getDate()).padStart(2, '0')}`;
      return transactionDate === dateStr;
    });
    
    const income = dayTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const expense = dayTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    return {
      transactions: dayTransactions,
      income,
      expense,
      balance: income - expense,
      count: dayTransactions.length
    };
  };

  // 이전/다음 달 이동
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // 날짜 클릭 처리
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // 오늘 날짜 확인
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // 현재 월인지 확인
  const isCurrentMonth = (date) => {
    return date.getMonth() === month;
  };

  // 선택된 날짜인지 확인
  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const monthNames = [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ];

  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div>
      {/* 달력 헤더 */}
      <div className="card calendar-view-header">
        <div className="flex-between mb-16">
          <div>
            <h2 className="calendar-view-title">
              <Icon name="calendar_today" size={32} />
              {year}년 {monthNames[month]}
            </h2>
            <p className="calendar-view-subtitle">
              일별 거래 내역을 확인해보세요
            </p>
          </div>
          
          <div className="calendar-view-nav">
            <button
              className="btn-icon btn-secondary"
              onClick={() => {
                const date = new Date(year, month - 1, 1);
                setCurrentDate(date);
              }}
            >
              ‹
            </button>
            
            <div className="date-picker-wrapper">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                  setPickerYear(year);
                }}
              >
                <Icon name="calendar_today" size={16} /> {year}년 {monthNames[month]}
              </button>
              
              {showDatePicker && (
                <div className="date-picker date-picker--dropdown">
                  {/* 년도 네비게이션 */}
                  <div className="flex-between mb-16">
                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear - 1)}
                    >
                      ‹
                    </button>
                    
                    <h3 className="card-title">
                      {pickerYear}년
                    </h3>
                    
                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear + 1)}
                    >
                      ›
                    </button>
                  </div>
                  
                  {/* 월 그리드 */}
                  <div className="date-picker-grid mb-16">
                    {Array.from({ length: 12 }, (_, monthIndex) => {
                      const isMonthSelected = pickerYear === year && monthIndex === month;
                      
                      return (
                        <button
                          key={monthIndex}
                          className={`date-picker-item${isMonthSelected ? ' date-picker-item--selected' : ''}`}
                          onClick={() => {
                            setCurrentDate(new Date(pickerYear, monthIndex, 1));
                            setShowDatePicker(false);
                          }}
                        >
                          {monthNames[monthIndex]}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* 이번 달 버튼 */}
                  <div className="flex-center date-picker__footer">
                    <button
                      className="date-picker-item"
                      onClick={() => {
                        const now = new Date();
                        setCurrentDate(now);
                        setPickerYear(now.getFullYear());
                        setShowDatePicker(false);
                      }}
                    >
                      이번 달
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <button
              className="btn-icon btn-secondary"
              onClick={() => {
                const date = new Date(year, month + 1, 1);
                setCurrentDate(date);
              }}
            >
              ›
            </button>
          </div>
        </div>

        {/* 달력 그리드 */}
        <div className="calendar-grid">
          {/* 요일 헤더 */}
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
          
          {/* 날짜 셀 */}
          {calendarDays.map((date, index) => {
            const dateData = getDateData(date);
            const isCurrentMonthDate = isCurrentMonth(date);
            const isTodayDate = isToday(date);
            const isSelectedDate = isSelected(date);
            
            const cellClass = [
              'calendar-day',
              !isCurrentMonthDate && 'other-month',
              isTodayDate && 'today',
              isSelectedDate && 'selected'
            ].filter(Boolean).join(' ');
            
            return (
              <div
                key={index}
                className={cellClass}
                onClick={() => handleDateClick(date)}
              >
                <div className="calendar-day__header">
                  <span className="calendar-day__number">
                    {date.getDate()}
                  </span>
                  {dateData.count > 0 && (
                    <div className="calendar-day__indicators">
                      {dateData.transactions.filter(t => t.type === 'income').length > 0 && (
                        <div className="calendar-day__indicator calendar-day__indicator--income">
                          {dateData.transactions.filter(t => t.type === 'income').length}
                        </div>
                      )}
                      {dateData.transactions.filter(t => t.type === 'expense').length > 0 && (
                        <div className="calendar-day__indicator calendar-day__indicator--expense">
                          {dateData.transactions.filter(t => t.type === 'expense').length}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {dateData.count > 0 && (
                  <div className="calendar-day__amounts">
                    {dateData.income > 0 && (
                      <div className="calendar-amount calendar-amount--income">
                        +{dateData.income.toLocaleString('ko-KR')}
                      </div>
                    )}
                    {dateData.expense > 0 && (
                      <div className="calendar-amount calendar-amount--expense">
                        -{dateData.expense.toLocaleString('ko-KR')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 선택된 날짜 상세 정보 */}
      {selectedDate && (
        <div className="card">
          <h3 className="mb-16">
            <Icon name="receipt_long" /> {selectedDate.toLocaleDateString('ko-KR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              weekday: 'long'
            })} 상세
          </h3>
          
          {(() => {
            const dateData = getDateData(selectedDate);
            
            if (dateData.count === 0) {
              return (
                <div className="empty-state">
                  <p>선택한 날짜에 거래 내역이 없습니다.</p>
                </div>
              );
            }

            return (
              <>
                {/* 일일 요약 */}
                <div className="grid-2 mb-16">
                  <div className="calendar-summary-card calendar-summary-card--income">
                    <div className="calendar-summary-card__label">
                      수입
                    </div>
                    <div className="calendar-summary-card__amount calendar-summary-card__amount--income">
                      +{dateData.income.toLocaleString('ko-KR')}원
                    </div>
                  </div>
                  <div className="calendar-summary-card calendar-summary-card--expense">
                    <div className="calendar-summary-card__label">
                      지출
                    </div>
                    <div className="calendar-summary-card__amount calendar-summary-card__amount--expense">
                      -{dateData.expense.toLocaleString('ko-KR')}원
                    </div>
                  </div>
                </div>

                {/* 거래 내역 */}
                <div>
                  <h4 className="mb-12">거래 내역 ({dateData.count}건)</h4>
                  <div className="transaction-list">
                    {dateData.transactions
                      .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date))
                      .map((transaction, index) => (
                        <div key={transaction.id || index} className="transaction-item">
                          <div>
                            <div className="transaction-category">
                              {transaction.description || transaction.category}
                            </div>
                            <div className="transaction-description">
                              <span>{transaction.category}</span>
                              <span> • </span>
                              <span>{new Date(transaction.created_at || transaction.date).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>
                          <div>
                            <div className={transaction.type === 'income' ? 'income' : 'expense'}>
                              {transaction.type === 'income' ? '+' : '-'}
                              {Number(transaction.amount).toLocaleString('ko-KR')}원
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default CalendarView;
